import * as React from 'react';
import {
    Alert, Box, Button, Chip, Link as MuiLink, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, getBetupFeedMessageReports, getBetupFeedPostReports, hideBetupFeedPost,
    rejectBetupFeedPost, resolveBetupFeedMessageReport, resolveBetupFeedPostReports, unhideBetupFeedPost,
} from '../Api';
import {
    BetupFeedMessageReportDto, BetupFeedReportedPostDto, BetupFeedReportStatusFilter,
} from '../models/dtos/BetupFeedPostDto';
import {
    ConfirmDialog, FeedImage, FeedPlayer, formatFeedDate, LinkedEntity, loadErrorText, PostText, ReviewStatusChip,
    shorten, RejectPostDialog, ReviewedByChip,
} from '../components/betupFeed/BetupFeedShared';

const TABS = [
    { query: 'posts', label: 'Posts' },
    { query: 'messages', label: 'Comments & chat' },
];

const SOURCE_LABELS: Record<string, string> = {
    FEED_COMMENT: 'Feed comment',
    MATCH_CHAT: 'Match chat',
    PRIVATE_MESSAGE: 'Private message',
};

type PendingAction =
    | { kind: 'hide' | 'unhide' | 'resolvePost' | 'reject'; row: BetupFeedReportedPostDto }
    | { kind: 'resolveMessage'; row: BetupFeedMessageReportDto }
    | null;

const ACTION_COPY: Record<NonNullable<PendingAction>['kind'], { title: string; confirm: string; color: 'primary' | 'error' | 'success' | 'warning'; text: string }> = {
    hide: {
        title: 'Hide post?', confirm: 'Hide', color: 'error',
        text: 'The post disappears from the feed and the profile for everyone, its author included. Unhide brings it back.',
    },
    unhide: {
        title: 'Unhide post?', confirm: 'Unhide', color: 'primary',
        text: 'The post comes back to the feed if it is approved (a pending or rejected post stays unpublished).',
    },
    reject: { title: 'Reject post?', confirm: 'Reject', color: 'error', text: '' },
    resolvePost: {
        title: 'Mark reports reviewed?', confirm: 'Mark reviewed', color: 'success',
        text: 'All open reports on this post are marked reviewed. The post itself is not changed.',
    },
    resolveMessage: {
        title: 'Mark report reviewed?', confirm: 'Mark reviewed', color: 'success',
        text: 'The report leaves the open list. The message itself is not changed.',
    },
};

export default function BetupFeedReportsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabIndex = Math.max(0, TABS.findIndex(t => t.query === searchParams.get('tab')));
    const statusFilter: BetupFeedReportStatusFilter = searchParams.get('status') === 'all' ? 'ALL' : 'OPEN';

    const [postRows, setPostRows] = React.useState<BetupFeedReportedPostDto[]>([]);
    const [messageRows, setMessageRows] = React.useState<BetupFeedMessageReportDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const offset = paginationModel.page * paginationModel.pageSize;
        if (tabIndex === 0) {
            const page = await getBetupFeedPostReports(statusFilter, offset, paginationModel.pageSize);
            setLoading(false);
            if (page == null) {
                setError(loadErrorText(BETUP_FEED_LABELS.postReports, 'Failed to load post reports.'));
                setPostRows([]);
                setRowCount(0);
                return;
            }
            setPostRows(page.items ?? []);
            setRowCount(page.totalItems ?? 0);
        } else {
            const page = await getBetupFeedMessageReports(statusFilter, offset, paginationModel.pageSize);
            setLoading(false);
            if (page == null) {
                setError(loadErrorText(BETUP_FEED_LABELS.messageReports, 'Failed to load message reports.'));
                setMessageRows([]);
                setRowCount(0);
                return;
            }
            setMessageRows(page.items ?? []);
            setRowCount(page.totalItems ?? 0);
        }
    }, [tabIndex, statusFilter, paginationModel]);

    React.useEffect(() => { load(); }, [load]);

    const updateQuery = (tab: number, status: BetupFeedReportStatusFilter) => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setError(null);
        setNotice(null);
        setSearchParams({ tab: TABS[tab].query, status: status === 'ALL' ? 'all' : 'open' }, { replace: true });
    };

    const runAction = async (reason?: string, categories?: string[]) => {
        if (pending == null) return;
        const action = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        let result: { error: string | null };
        let done: string;
        switch (action.kind) {
            case 'hide':
                result = await hideBetupFeedPost(action.row.post.id);
                done = `Post #${action.row.post.id} hidden.`;
                break;
            case 'unhide':
                result = await unhideBetupFeedPost(action.row.post.id);
                done = `Post #${action.row.post.id} unhidden.`;
                break;
            case 'reject':
                result = await rejectBetupFeedPost(action.row.post.id, reason ?? '', categories ?? []);
                done = `Post #${action.row.post.id} rejected and its reports marked reviewed — the author sees your reason.`;
                break;
            case 'resolvePost':
                result = await resolveBetupFeedPostReports(action.row.post.id);
                done = `Reports on post #${action.row.post.id} marked reviewed.`;
                break;
            case 'resolveMessage':
                result = await resolveBetupFeedMessageReport(action.row.id);
                done = `Message report #${action.row.id} marked reviewed.`;
                break;
        }
        setBusy(false);
        if (result.error != null) {
            setError(`Action failed: ${result.error}`);
            return;
        }
        setError(null);
        setNotice(done);
        await load();
    };

    const postColumns: GridColDef<BetupFeedReportedPostDto>[] = [
        {
            field: 'author', headerName: 'Author', width: 190, sortable: false,
            renderCell: ({ row }) => <FeedPlayer player={row.author} dense />,
        },
        {
            field: 'post', headerName: 'Post', flex: 1, minWidth: 280, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1} sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                        post #{row.post.id} · {formatFeedDate(row.post.created_at)}
                    </Typography>
                    <PostText text={row.post.text} />
                    <LinkedEntity match={row.post.match} team={row.post.team} />
                </Stack>
            ),
        },
        {
            field: 'image', headerName: 'Image', width: 116, sortable: false,
            renderCell: ({ row }) => <FeedImage url={row.post.image_url} expired={row.post.image_expired} />,
        },
        {
            field: 'reports', headerName: 'Reports', width: 250, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.75} sx={{ width: '100%' }}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        <Chip size="small" color={row.open_reports_count > 0 ? 'error' : 'default'}
                            label={`${row.open_reports_count} open`} />
                        <Chip size="small" variant="outlined" label={`${row.reports_count} total`} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">last {formatFeedDate(row.last_reported_at)}</Typography>
                    {(row.reasons ?? []).map((r, i) => (
                        <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            • {r.reason || <i>no reason</i>}
                            <Typography component="span" variant="caption" color="text.secondary">
                                {' '}— by <MuiLink component={RouterLink} to={`/users/${r.reporter_id}`} underline="hover">#{r.reporter_id}</MuiLink>, {formatFeedDate(r.created_at)}
                            </Typography>
                        </Typography>
                    ))}
                </Stack>
            ),
        },
        {
            field: 'state', headerName: 'State', width: 200, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.5}>
                    {row.post.review_status && (
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                            <ReviewStatusChip status={row.post.review_status} />
                            <ReviewedByChip reviewer={row.reviewed_by} />
                        </Stack>
                    )}
                    {row.post.review_status === 'REJECTED' && row.post.review_reason && (
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{row.post.review_reason}</Typography>
                    )}
                    {row.hidden
                        ? <Chip size="small" color="warning" label={`hidden ${formatFeedDate(row.hidden_at)}`} />
                        : <Chip size="small" variant="outlined" label="visible" />}
                    {row.deleted && <Chip size="small" color="error" variant="outlined" label={`deleted ${formatFeedDate(row.deleted_at)}`} />}
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 150, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1}>
                    {row.post.review_status !== 'REJECTED' && (
                        <Button size="small" variant="contained" color="error" disabled={busy || row.deleted}
                            onClick={() => setPending({ kind: 'reject', row })}>Reject</Button>
                    )}
                    {row.hidden ? (
                        <Button size="small" variant="outlined" disabled={busy || row.deleted}
                            onClick={() => setPending({ kind: 'unhide', row })}>Unhide</Button>
                    ) : (
                        <Button size="small" variant="outlined" color="warning" disabled={busy || row.deleted}
                            onClick={() => setPending({ kind: 'hide', row })}>Hide</Button>
                    )}
                    <Button size="small" variant="outlined" color="success" disabled={busy || row.open_reports_count === 0}
                        onClick={() => setPending({ kind: 'resolvePost', row })}>Mark reviewed</Button>
                </Stack>
            ),
        },
    ];

    const messageColumns: GridColDef<BetupFeedMessageReportDto>[] = [
        {
            field: 'source', headerName: 'Source', width: 130, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.5}>
                    <Chip size="small" variant="outlined" color={row.source === 'MATCH_CHAT' ? 'info' : 'secondary'}
                        label={SOURCE_LABELS[row.source] ?? row.source} />
                    <Typography variant="caption" color="text.secondary">report #{row.id}</Typography>
                </Stack>
            ),
        },
        {
            field: 'text', headerName: 'Message', flex: 1, minWidth: 260, sortable: false,
            renderCell: ({ row }) => <PostText text={row.text} />,
        },
        {
            field: 'reason', headerName: 'Reason', width: 180, sortable: false,
            renderCell: ({ row }) => row.reason
                ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.reason}</Typography>
                : <Typography variant="body2" color="text.secondary"><i>no reason</i></Typography>,
        },
        {
            field: 'author', headerName: 'Author', width: 200, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.5}>
                    <FeedPlayer player={row.author} dense />
                    <Chip size="small" color={row.author_reports_count > 1 ? 'warning' : 'default'} variant="outlined"
                        label={`${row.author_reports_count} report${row.author_reports_count === 1 ? '' : 's'} on author`} />
                </Stack>
            ),
        },
        {
            field: 'reporter', headerName: 'Reporter', width: 180, sortable: false,
            renderCell: ({ row }) => <FeedPlayer player={row.reporter} dense />,
        },
        {
            field: 'dates', headerName: 'Reported / reviewed', width: 170, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{formatFeedDate(row.created_at)}</Typography>
                    {row.resolved_at
                        ? <Typography variant="caption" color="success.main">reviewed {formatFeedDate(row.resolved_at)}</Typography>
                        : <Typography variant="caption" color="error.main">open</Typography>}
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 140, sortable: false,
            renderCell: ({ row }) => row.resolved_at ? null : (
                <Button size="small" variant="outlined" color="success" disabled={busy}
                    onClick={() => setPending({ kind: 'resolveMessage', row })}>Mark reviewed</Button>
            ),
        },
    ];

    const copy = pending ? ACTION_COPY[pending.kind] : null;
    const pendingSubject = pending == null ? ''
        : pending.kind === 'resolveMessage'
            ? `${SOURCE_LABELS[pending.row.source] ?? pending.row.source} by ${pending.row.author?.name ?? 'unknown'}: “${shorten(pending.row.text)}”`
            : `Post #${pending.row.post.id} by ${pending.row.author?.name ?? 'unknown'}: “${shorten(pending.row.post.text)}”`;

    const grid = {
        autoHeight: true,
        getRowHeight: () => 'auto' as const,
        paginationMode: 'server' as const,
        rowCount,
        paginationModel,
        onPaginationModelChange: setPaginationModel,
        pageSizeOptions: [10, 20, 50],
        loading,
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        sx: { '& .MuiDataGrid-cell': { py: 1.5, alignItems: 'flex-start' } },
    };

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">BETUP Feed — reports</Typography>
            <Typography variant="body2" color="text.secondary">
                Player reports on posts, feed comments and match chat messages. Message reports carry the text as the
                reporter saw it (the message itself lives in the messaging service). Reject overrides Claude's approval:
                the post leaves the feed with your reason and categories and the author can no longer edit it; Hide only
                takes it out of sight and can be undone; Mark reviewed dismisses the reports and keeps the post.
            </Typography>
            <Alert severity="info">Blocking a user from the feed or chat is not available yet — it will come in a later release.</Alert>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Tabs value={tabIndex} onChange={(_, value: number) => updateQuery(value, statusFilter)}>
                    {TABS.map(t => <Tab key={t.query} label={t.label} />)}
                </Tabs>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={statusFilter}
                    onChange={(_, value: BetupFeedReportStatusFilter | null) => { if (value) updateQuery(tabIndex, value); }}
                >
                    <ToggleButton value="OPEN">Open</ToggleButton>
                    <ToggleButton value="ALL">All</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Box>
                {tabIndex === 0 ? (
                    <DataGrid
                        key="posts"
                        {...grid}
                        rows={postRows}
                        columns={postColumns}
                        getRowId={row => row.post.id}
                        localeText={{ noRowsLabel: statusFilter === 'OPEN' ? 'No open post reports' : 'No reported posts' }}
                    />
                ) : (
                    <DataGrid
                        key="messages"
                        {...grid}
                        rows={messageRows}
                        columns={messageColumns}
                        getRowId={row => row.id}
                        localeText={{ noRowsLabel: statusFilter === 'OPEN' ? 'No open message reports' : 'No message reports' }}
                    />
                )}
            </Box>
            <RejectPostDialog
                open={pending?.kind === 'reject'}
                subject={pendingSubject}
                onClose={() => setPending(null)}
                onConfirm={(reason, categories) => runAction(reason, categories)}
            />
            <ConfirmDialog
                open={pending != null && pending.kind !== 'reject'}
                title={copy?.title ?? ''}
                confirmLabel={copy?.confirm ?? 'OK'}
                confirmColor={copy?.color ?? 'primary'}
                onClose={() => setPending(null)}
                onConfirm={() => runAction()}
                text={<>{pendingSubject}<br />{copy?.text}</>}
            />
        </Stack>
    );
}
